import { useTable } from '@refinedev/antd';
import { Table, Tag } from 'antd';
import { Inbox } from 'lucide-react';
import { useState, type FC } from 'react';
import FeedbackDetailPanel from './FeedbackDetailPanel';

interface FeedbackRecord {
  id: string;
  title: string;
  type: string;
  status: string;
  priority: string;
  createdAt: string;
}

const STATUS_COLORS: Record<string, string> = {
  new: 'cyan',
  analyzing: 'geekblue',
  analyzed: 'blue',
  in_progress: 'orange',
  resolved: 'green',
  closed: 'default',
  rejected: 'red',
};

const FeedbackInboxPage: FC = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { tableProps } = useTable<FeedbackRecord>({
    resource: 'admin/feedbacks',
    pagination: { pageSize: 15 },
  });

  return (
    <div className="space-y-6">
      {/* Header gradient */}
      <div className="fc-gradient-header rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3">
          <Inbox className="h-6 w-6" />
          <div>
            <h1 className="text-xl font-bold">Feedback Inbox</h1>
            <p className="mt-1 text-sm text-blue-100">Flux des retours utilisateurs</p>
          </div>
        </div>
      </div>

      {/* Split view */}
      <div className="flex gap-4">
        {/* Left — List */}
        <div
          className={`fc-card overflow-hidden p-0 transition-all ${selectedId ? 'w-1/2' : 'w-full'}`}
        >
          <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              Tous les feedbacks
            </h3>
          </div>
          <Table
            {...tableProps}
            rowKey="id"
            size="small"
            onRow={(record) => ({
              onClick: () => setSelectedId(record.id),
              style: { cursor: 'pointer' },
              className: record.id === selectedId ? 'ant-table-row-selected' : '',
            })}
          >
            <Table.Column dataIndex="title" title="Titre" ellipsis />
            <Table.Column
              dataIndex="status"
              title="Statut"
              width={120}
              render={(s: string) => <Tag color={STATUS_COLORS[s] ?? 'default'}>{s}</Tag>}
            />
            <Table.Column
              dataIndex="createdAt"
              title="Date"
              width={100}
              render={(d: string) => (
                <span className="text-slate-500 dark:text-slate-400">
                  {d ? new Date(d).toLocaleDateString('fr-FR') : '—'}
                </span>
              )}
            />
          </Table>
        </div>

        {/* Right — Detail panel */}
        {selectedId && (
          <div className="fc-card w-1/2 overflow-hidden p-0">
            <FeedbackDetailPanel feedbackId={selectedId} onClose={() => setSelectedId(null)} />
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackInboxPage;
